import { z } from 'zod';

/**
 * 全サーバー共通の IP BAN。edge の nftables で、ゲームの通信（Java 版・統合版・追加ポート）を
 * 入口で落とす。Web（パネル）や SFTP は落とさない（自分を締め出さないため）。
 */
const Single = z.union([z.ipv4(), z.ipv6()]);
const Range = z.union([z.cidrv4(), z.cidrv6()]);
export const NetworkBanAddressSchema = z
  .string()
  .trim()
  .toLowerCase()
  .refine((value) => Single.safeParse(value).success || Range.safeParse(value).success, {
    message: 'IP アドレス（例: 203.0.113.5）か範囲（例: 203.0.113.0/24）を入れてください',
  })
  .refine(
    (value) => {
      const bits = /\/(\d+)$/.exec(value)?.[1];
      return bits === undefined || Number(bits) >= (isIpv6Ban(value) ? 32 : 8);
    },
    { message: '範囲が広すぎます（IPv4 は /8、IPv6 は /32 まで）' },
  )
  .refine((value) => !isPrivateAddress(value), {
    message: '家の中（LAN）やループバックのアドレスは BAN できません（edge やサーバー自身を止めてしまうため）',
  })
  .transform(normalizeRange);

/** LAN・ループバック・リンクローカル。Velocity を通った接続の送り元がこれなら、本当の IP ではない */
export function isPrivateAddress(value: string): boolean {
  const address = value.split('/')[0]!.toLowerCase();
  if (isIpv6Ban(address))
    return address === '::1' || /^f[cd]/.test(address) || /^fe[89ab]/.test(address) || address === '::';
  const [a, b] = address.split('.').map(Number);
  return (
    a === 10 ||
    a === 127 ||
    a === 0 ||
    (a === 172 && b! >= 16 && b! <= 31) ||
    (a === 192 && b === 168) ||
    (a === 169 && b === 254)
  );
}

/** ログイン時のログ（`Steve[/203.0.113.5:54321] logged in with entity id ...`）から名前と IP を読む */
export function parseLoginAddress(line: string): { name: string; address: string } | undefined {
  // 行頭のログの前置き（[時刻 INFO]: や [時刻] [Server thread/INFO]:）の直後だけを見る。
  // チャットの中に同じ文を書いてほかの人の IP をすり替えられないようにする。
  const match = /^\[[^\]]*\](?: \[[^\]]*\])?: ([.*]?\w{1,16})\[\/([^\]\s]+\]?[^\]\s]*)\] logged in with entity id/.exec(
    line,
  );
  if (!match) return undefined;
  const raw = match[2]!;
  const address = raw.startsWith('[') ? raw.slice(1, raw.indexOf(']')) : raw.slice(0, raw.lastIndexOf(':'));
  return Single.safeParse(address).success ? { name: match[1]!, address: address.toLowerCase() } : undefined;
}

/** 範囲の端数（ホスト部）を 0 にそろえる。nftables は端数つきの範囲を受け付けないことがあるため */
export function normalizeRange(value: string): string {
  const [address, bitsText] = value.split('/');
  if (bitsText === undefined) return value;
  const bits = Number(bitsText);
  if (!isIpv6Ban(value)) {
    const n = address!.split('.').reduce((sum, part) => (sum << 8n) + BigInt(part), 0n);
    const masked = n & (((1n << 32n) - 1n) ^ ((1n << BigInt(32 - bits)) - 1n));
    return `${[24n, 16n, 8n, 0n].map((shift) => String((masked >> shift) & 255n)).join('.')}/${bits}`;
  }
  const [head, tail = ''] = address!.split('::');
  const left = head ? head.split(':') : [];
  const right = tail ? tail.split(':') : [];
  const groups = address!.includes('::')
    ? [...left, ...Array<string>(8 - left.length - right.length).fill('0'), ...right]
    : left;
  const n = groups.reduce((sum, group) => (sum << 16n) + BigInt(parseInt(group || '0', 16)), 0n);
  const masked = n & (((1n << 128n) - 1n) ^ ((1n << BigInt(128 - bits)) - 1n));
  const out = Array.from({ length: 8 }, (_, i) => ((masked >> BigInt(112 - i * 16)) & 0xffffn).toString(16));
  return `${out.join(':')}/${bits}`;
}
const Reason = z
  .string()
  .max(200)
  .refine((r) => !/[\r\n\0]/.test(r), '理由に改行は使えません')
  .optional();
export const NetworkBanInputSchema = z.object({ address: NetworkBanAddressSchema, reason: Reason }).strict();
export type NetworkBanInput = z.infer<typeof NetworkBanInputSchema>;
export const NetworkBanSchema = z.object({
  address: z.string(),
  reason: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
});
export type NetworkBan = z.infer<typeof NetworkBanSchema>;
/** BAN の対象にする edge のポート。Java 版の入口、統合版、追加ポート（ボイスチャットなど） */
export const NETWORK_BAN_PORTS = { tcp: ['25565', '24400-24499'], udp: ['19132-19231', '24400-24499'] } as const;

export function isIpv6Ban(address: string) {
  return address.includes(':');
}

/** nftables の表。中身は検証済みの値だけ（文字列をそのまま埋め込むので、ここで必ず検証する） */
export function generateBansNft(addresses: string[]): string {
  const checked = [...new Set(addresses.map((a) => NetworkBanAddressSchema.parse(a)))].sort();
  // BAN がなくても表は消す（最後の 1 件を外したときに古い表が残らないように）
  if (!checked.length) return ['add table inet mc_bans', 'delete table inet mc_bans'].join('\n');
  const v4 = checked.filter((a) => !isIpv6Ban(a));
  const v6 = checked.filter(isIpv6Ban);
  const set = (name: string, type: string, items: string[]) => [
    `  set ${name} {`,
    `    type ${type}; flags interval; auto-merge;`,
    ...(items.length ? [`    elements = { ${items.join(', ')} }`] : []),
    '  }',
  ];
  return [
    'add table inet mc_bans',
    'delete table inet mc_bans',
    'table inet mc_bans {',
    ...set('v4', 'ipv4_addr', v4),
    ...set('v6', 'ipv6_addr', v6),
    '  chain prerouting {',
    '    type filter hook prerouting priority raw; policy accept;',
    `    ip saddr @v4 tcp dport { ${NETWORK_BAN_PORTS.tcp.join(', ')} } drop`,
    `    ip saddr @v4 udp dport { ${NETWORK_BAN_PORTS.udp.join(', ')} } drop`,
    `    ip6 saddr @v6 tcp dport { ${NETWORK_BAN_PORTS.tcp.join(', ')} } drop`,
    `    ip6 saddr @v6 udp dport { ${NETWORK_BAN_PORTS.udp.join(', ')} } drop`,
    '  }',
    '}',
  ].join('\n');
}
